import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CircleCheck, Paperclip, Send } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import SectionEyebrow from '../common/SectionEyebrow';
import FormField from '../common/FormField';
import Footer from '../Footer';
import '../../assets/styles/SectionBand.css';
import '../../assets/styles/FormCard.css';

const API = process.env.REACT_APP_API_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { organizacion: '', email: '', identificador: '', web: '', mision: '' };

const OngVerification = () => {
  const { t, language } = useLanguage();
  const [values, setValues] = useState(EMPTY);
  const [documento, setDocumento] = useState(null);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Se llega desde el registro o desde el perfil: la vuelta depende de si ya hay sesion
  const loggedIn = Boolean(localStorage.getItem('token'));

  const update = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }));
  };

  const handleFileChange = (event) => {
    setDocumento(event.target.files && event.target.files[0] ? event.target.files[0] : null);
    setErrors((prev) => ({ ...prev, form: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (sending) return;

    const nextErrors = {};
    if (!values.organizacion.trim()) nextErrors.organizacion = t('ong_verify_error_org');
    if (!values.email.trim()) nextErrors.email = t('auth_error_email_required');
    else if (!EMAIL_RE.test(values.email.trim())) nextErrors.email = t('auth_error_email_invalid');
    if (!values.identificador.trim()) nextErrors.identificador = t('ong_verify_error_id');
    if (!values.mision.trim()) nextErrors.mision = t('ong_verify_error_mission');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSending(true);
    try {
      const form = new FormData();
      Object.entries(values).forEach(([key, value]) => form.append(key, value.trim()));
      if (documento) form.append('documento', documento);
      form.append('idioma', language);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/ong-requests`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form
      });

      if (response.ok) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const data = await response.json().catch(() => ({}));
      if (data.field === 'email') setErrors({ email: t('auth_error_email_invalid') });
      else if (data.code === 'already_pending') setErrors({ form: t('ong_verify_error_pending') });
      else if (data.code === 'invalid_file') setErrors({ form: data.message });
      else setErrors({ form: t(response.status === 429 ? 'auth_error_too_many' : 'ong_verify_error_send') });
    } catch (error) {
      console.error('Error al enviar la solicitud de ONG:', error);
      setErrors({ form: t('ong_verify_error_send') });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="brand-page-stack tp-form-page">
      <section className="section-band">
        <div className="section-band__inner tp-form-layout">
          <div className="tp-form-layout__intro">
            <h1 className="tp-sr-only">{t('ong_verify_title')}</h1>
            <div aria-hidden="true">
              <SectionEyebrow>{t('ong_verify_kicker')}</SectionEyebrow>
            </div>
            <p className="tp-form-layout__statement">{t('ong_verify_title')}</p>
            <p className="section-band__lead">{t('ong_verify_intro')}</p>
          </div>

          {submitted ? (
            <div className="tp-form" role="status">
              <CircleCheck size={32} strokeWidth={1.75} color="#7C3AED" aria-hidden="true" />
              <p className="tp-form-layout__statement">{t('ong_verify_success_title')}</p>
              <p className="tp-form__copy">{t('ong_verify_success_copy')}</p>
              <Link to={loggedIn ? '/profile' : '/sign-up'} className="btn btn--outline tp-form__submit">
                <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
                {t(loggedIn ? 'ong_verify_back_profile' : 'ong_verify_back_signup')}
              </Link>
            </div>
          ) : (
            <form className="tp-form" onSubmit={handleSubmit} noValidate>
              <div className="tp-form__grid">
                <FormField
                  id="ong-org"
                  label={t('ong_verify_org_label')}
                  placeholder={t('ong_verify_org_placeholder')}
                  autoComplete="organization"
                  value={values.organizacion}
                  onChange={update('organizacion')}
                  error={errors.organizacion}
                  required
                />
                <FormField
                  id="ong-email"
                  type="email"
                  label={t('ong_verify_email_label')}
                  placeholder={t('ong_verify_email_placeholder')}
                  autoComplete="email"
                  value={values.email}
                  onChange={update('email')}
                  error={errors.email}
                  required
                />
                <FormField
                  id="ong-id"
                  label={t('ong_verify_id_label')}
                  placeholder={t('ong_verify_id_placeholder')}
                  value={values.identificador}
                  onChange={update('identificador')}
                  error={errors.identificador}
                  required
                />
                <FormField
                  id="ong-website"
                  type="url"
                  label={t('ong_verify_website_label')}
                  placeholder={t('ong_verify_website_placeholder')}
                  autoComplete="url"
                  value={values.web}
                  onChange={update('web')}
                />
              </div>

              <FormField
                id="ong-mission"
                as="textarea"
                rows={5}
                label={t('ong_verify_mission_label')}
                placeholder={t('ong_verify_mission_placeholder')}
                value={values.mision}
                onChange={update('mision')}
                error={errors.mision}
                required
              />

              <div className="tp-field">
                <label htmlFor="ong-document">{t('ong_verify_document_label')}</label>
                <div className="tp-file">
                  <input id="ong-document" type="file" accept=".pdf,.png,.jpg,.jpeg" className="tp-sr-only" onChange={handleFileChange} />
                  <label htmlFor="ong-document" className="btn btn--outline btn--sm tp-file__pick">
                    <Paperclip size={15} strokeWidth={2} aria-hidden="true" />
                    {t('ong_verify_document_pick')}
                  </label>
                  <span className="tp-file__name">{documento ? documento.name : t('ong_verify_document_hint')}</span>
                </div>
              </div>

              <p className="tp-form__note">{t('ong_verify_helper')}</p>

              {errors.form && <p className="tp-form__alert" role="alert">{errors.form}</p>}

              <button type="submit" className="btn btn--primary tp-form__submit" disabled={sending}>
                {sending ? t('ong_verify_sending') : t('ong_verify_submit')}
                <Send size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default OngVerification;
